/**
 * imageCompression.js
 * 
 * Utility functions for compressing images on the client side.
 * Uses Canvas API for lossy JPEG compression and resizing.
 */

/**
 * Compresses an image file using Canvas API
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width in pixels (default: 1920)
 * @param {number} maxHeight - Maximum height in pixels (default: 1920)
 * @param {number} quality - JPEG quality 0-1 (default: 0.7 for 30% size reduction)
 * @returns {Promise<{name, size, type, data}>} Compressed image object
 */
export const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsImage = true
    reader.onload = (event) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement("canvas")
        let width = img.width
        let height = img.height

        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height
          height = maxHeight
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            resolve({
              name: file.name,
              size: blob.size,
              type: "image/jpeg",
              data: blob,
              originalSize: file.size,
            })
          },
          "image/jpeg",
          quality
        )
      }
      img.onerror = () => reject(new Error("Failed to load image"))
      img.src = event.target.result
    }
    reader.onerror = () => reject(new Error("Failed to read file"))
    reader.readAsDataURL(file)
  })
}

/**
 * Converts a Blob to base64 data URL
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} Base64 data URL
 */
export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = () => reject(new Error("Failed to convert blob to base64"))
    reader.readAsDataURL(blob)
  })
}

/**
 * Compresses multiple image files
 * @param {FileList|Array} files - Files to compress
 * @param {number} maxWidth - Maximum width (default: 1920)
 * @param {number} maxHeight - Maximum height (default: 1920)
 * @param {number} quality - JPEG quality (default: 0.7)
 * @returns {Promise<Array>} Array of compressed photo objects with base64 data
 */
export const compressImages = async (
  files,
  maxWidth = 1920,
  maxHeight = 1920,
  quality = 0.7
) => {
  const compressedImages = await Promise.all(
    Array.from(files || []).map(async (file) => {
      try {
        const compressed = await compressImage(file, maxWidth, maxHeight, quality)
        const base64Data = await blobToBase64(compressed.data)
        return {
          name: compressed.name,
          size: compressed.size,
          type: compressed.type,
          data: base64Data,
          originalSize: compressed.originalSize,
        }
      } catch (error) {
        console.error(`Failed to compress ${file.name}:`, error)
        // Fallback to original file if compression fails
        return new Promise((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => {
            resolve({
              name: file.name,
              size: file.size,
              type: file.type,
              data: reader.result,
            })
          }
          reader.readAsDataURL(file)
        })
      }
    })
  )
  return compressedImages
}

/**
 * Calculate compression ratio
 * @param {number} originalSize - Original file size in bytes
 * @param {number} compressedSize - Compressed file size in bytes
 * @returns {number} Compression ratio as percentage (e.g., 0.7 = 30% reduction)
 */
export const calculateCompressionRatio = (originalSize, compressedSize) => {
  return 1 - compressedSize / originalSize
}
